import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { LazyLoadImageModule } from 'ng-lazyload-image';
import { TimeagoModule } from 'ngx-timeago';

import { DataService } from '@/services/data.service';
import { PunkWrapperService } from '@/services/punk-wrapper.service';
import { StateService } from '@/services/state.service';

import { Bid, Event, Listing, Punk } from '@/models/graph';

import { WeiToEthPipe } from '@/pipes/wei-to-eth.pipe';
import { FormatCashPipe } from '@/pipes/format-cash.pipe';
import { TokenIdParsePipe } from '@/pipes/token-id-parse.pipe';

import { BehaviorSubject, Observable, Subject, combineLatest, firstValueFrom, from, map, of, switchMap, takeUntil, tap, catchError, EMPTY, take, interval, startWith, shareReplay } from 'rxjs';
import { WalletAddressDirective } from '@/directives/wallet-address.directive';
import { FilterPipe } from '@/pipes/filter.pipe';
import { Web3Service } from '@/services/web3.service';
import { UsdPipe } from '@/pipes/usd.pipe';
import { environment } from 'src/environments/environment';
import { EnsMetadataComponent } from '@/components/ens-metadata/ens-metadata';
import { PlatformService } from '@/services/platform.service';
import { PUNKAUCTION_ADDRESS, TOKENWORKS_ADDRESS } from '@/constants/utils';
import {
  isPunksmarketNativeSale,
  punksmarketAppPunkUrl,
} from '@/constants/punksmarket-v1';
import { isOwnedPunkWrapped, withDashboardSaleDateTippyOptions, DashboardSaleEvent } from '@/utils/dashboard-sale.util';
import { OnChainBidLabelComponent } from '@/components/on-chain-bid-label/on-chain-bid-label.component';
import {
  nativeBidTypeLabelPlural,
  onChainBidCancelFailure,
  onChainBidCancelSuccess,
  onChainBidOutgoingSectionTitle,
} from '@/utils/native-market-bid-labels';
import { ThemeService } from '@/services/theme.service';
import { MarketplaceIconsComponent } from '@/components/marketplace-icons/marketplace-icons.component';
import { BidCreationModalComponent, BidType, CopyFromBidPayload } from '@/components/bid-creation-modal/bid-creation-modal.component';
import { BidCopyConfirmationModalComponent } from '@/components/bid-copy-confirmation-modal/bid-copy-confirmation-modal.component';
import { ConfirmationDialogComponent } from '@/components/confirmation-dialog/confirmation-dialog.component';
import { TippyDirective } from '@/directives/tippy.directive';
import { StashBidsTableComponent, StashBid } from '@/components/stash-bids-table/stash-bids-table.component';
import { PunksmarketV1Service } from '@/services/punksmarket-v1.service';
import {
  mapPunksmarketIndexerBidToUnified,
  unifiedBidToStashTableShape,
} from '@/utils/unified-market-bid.mappers';
import { SelectPunkModalComponent } from '@/components/select-punk-modal/select-punk-modal.component';
import { TransactionReceipt } from 'viem';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    EnsMetadataComponent,
    LazyLoadImageModule,
    TimeagoModule,

    WalletAddressDirective,

    WeiToEthPipe,
    FilterPipe,
    FormatCashPipe,
    TokenIdParsePipe,
    UsdPipe,
    MarketplaceIconsComponent,
    BidCreationModalComponent,
    StashBidsTableComponent,
    SelectPunkModalComponent,
    BidCopyConfirmationModalComponent,
    ConfirmationDialogComponent,
    TippyDirective,
    OnChainBidLabelComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})

export class DashboardComponent implements OnDestroy {
  readonly nativeBidTypeLabelPlural = nativeBidTypeLabelPlural;
  readonly onChainBidOutgoingSectionTitle = onChainBidOutgoingSectionTitle;

  owned$!: Observable<Punk[]>;
  private ownedPunksSubject = new BehaviorSubject<Punk[]>([]);
  private viewedProfileAddress: string | null = null;
  listings$!: Observable<Listing[]>;
  bids$!: Observable<Bid[]>;
  
  bought$!: Observable<DashboardSaleEvent[]>;
  sold$!: Observable<DashboardSaleEvent[]>;

  // Stash bids observables
  stashBidsReceived$!: Observable<any[]>;
  stashBidsPlaced$!: Observable<any[]>;
  punksmarketBidsPlaced$!: Observable<StashBid[]>;

  // New observables for proxy punks
  public proxyPunks$!: Observable<Punk[]>;
  public c721ProxyPunks$!: Observable<Punk[]>;
  public legacyProxyPunks$!: Observable<Punk[]>;

  destroy$ = new Subject<void>();

  calcs: any = {};
  loading: boolean = true;

  // User connection state
  public isUserConnected: boolean = false;
  public userAddress: string | null = null;
  private chainId: number = 1; // Default to mainnet
  
  // Store owned punk IDs for bid acceptance validation
  private ownedPunkIds: number[] = [];
  
  // Observable to determine if accept button should be shown
  showAcceptButton$!: Observable<boolean>;
  
  // Properties for punk selection modal
  selectedStashBid: any | null = null;
  matchingPunkIdsForBid: number[] = [];
  selectPunkForBidModalActive: boolean = false;

  TOKENWORKS_ADDRESS = TOKENWORKS_ADDRESS
  PUNKAUCTION_ADDRESS = PUNKAUCTION_ADDRESS
  readonly tokenworksAddressLower = TOKENWORKS_ADDRESS.toLowerCase();
  readonly punkauctionAddressLower = PUNKAUCTION_ADDRESS.toLowerCase();

  // Owner bid modal state
  ownerBidModalActive: boolean = false;
  ownerBidAddress: string | null = null;
  ownerBidPunkIndices: number[] = [];
  ownerBidFilterTag: string = '';

  // Copy bid modal state
  copyBidModalActive: boolean = false;
  copyFromBidPayload: CopyFromBidPayload | null = null;
  copyConfirmationModalActive: boolean = false;
  copyConfirmationPunkIds: number[] = [];

  // Stash address computation
  stashAddress$!: Observable<string | null>;
  
  // Resolved address for the current route (ENS -> address)
  resolvedAddress$!: Observable<string | null>;

  // Whether the user has deployed their stash (null when unknown)
  stashDeployed$!: Observable<boolean | null>;

  // Balance observables (for the viewed address, not only the connected user)
  walletBalance$!: Observable<string | null>;
  stashBalance$!: Observable<string | null>;
  
  /** Same key as my-bids component: "display only bids above min ETH" setting. */
  private readonly MY_BIDS_STORAGE_KEY_MIN_ETH = 'my-bids-display-only-bids-above-min-eth';
  private readonly STASH_BIDS_DISPLAY_ARCHIVED_STORAGE_KEY = 'dashboard-stash-bids-display-archived';

  displayArchivedStashBids: boolean = false;
  private displayArchivedStashBids$ = new BehaviorSubject<boolean>(false);
  private stashBidsPlacedRefresh$ = new BehaviorSubject<number>(0);
  private punksmarketBidsPlacedRefresh$ = new BehaviorSubject<number>(0);
  punksmarketCancelInProgress = false;
  outgoingCancelInProgress: boolean = false;
  cancelDialogActive: boolean = false;
  cancelDialogTitle: string = 'Confirm action';
  cancelDialogMessage: string = '';
  cancelDialogConfirmLabel: string = 'Confirm';
  cancelDialogShowCancelButton: boolean = true;
  cancelDialogDestructive: boolean = true;
  private pendingCancelAction: { mode: 'single' | 'all'; bidNonce?: number } | null = null;

  constructor(
    public dataSvc: DataService,
    public route: ActivatedRoute,
    private router: Router,
    private weiToEth: WeiToEthPipe,
    private web3Svc: Web3Service,
    private usdPipe: UsdPipe,
    private platformService: PlatformService,
    private punkWrapperService: PunkWrapperService,
    public stateService: StateService,
    protected themeSvc: ThemeService,
    private punksmarketV1Svc: PunksmarketV1Service,
  ) {
    this.loading = true;
    
    // Initialize proxy observables with empty arrays
    this.proxyPunks$ = of([]);
    this.c721ProxyPunks$ = of([]);
    this.legacyProxyPunks$ = of([]);
    
    // Initialize stash bids observables with empty arrays
    this.stashBidsReceived$ = of([]);
    this.stashBidsPlaced$ = of([]);
    this.punksmarketBidsPlaced$ = of([]);

    this.initializeDashboard();
  }

  private initializeDashboard(): void {
    this.restoreDisplayArchivedStashBidsState();
    this.owned$ = this.ownedPunksSubject.asObservable();
    window.addEventListener('ownedWrapStatusChanged', this.handleOwnedWrapStatusChanged);

    // Resolve route address (supports ENS)
    this.resolvedAddress$ = this.route.params.pipe(
      switchMap((params: any) => {
        const raw = params.address as string | undefined;
        if (!raw) {
          return of(null);
        }
        return from(this.web3Svc.verifyAddressOrEns(raw)).pipe(
          catchError((err) => {
            console.error('Error resolving route address:', err);
            return of(null);
          })
        );
      }),
      map((addr) => (addr ? addr.toLowerCase() : null)),
      shareReplay(1),
      takeUntil(this.destroy$)
    );

    // Stash address from chain via environment.stashFactoryAddress (v2 vs v1 ERC721 factory, etc.)
    this.stashAddress$ = this.resolvedAddress$.pipe(
      switchMap((resolvedAddress) => {
        if (!resolvedAddress || environment.featureFlags?.STASH_BID_SUPPORT === false) {
          return of(null);
        }
        return from(this.web3Svc.getStashAddressFor(resolvedAddress)).pipe(
          catchError((err) => {
            console.error('Error resolving stash address for dashboard:', err);
            return of(null);
          })
        );
      }),
      shareReplay(1),
      takeUntil(this.destroy$)
    );

    // Determine whether the viewed user has deployed their stash
    this.stashDeployed$ = this.resolvedAddress$.pipe(
      switchMap((resolvedAddress) => {
        if (!resolvedAddress) {
          return of(null);
        }
        return from(this.web3Svc.checkStashDeployed(resolvedAddress)).pipe(
          catchError((err) => {
            console.error('Error checking stash deployment:', err);
            return of(null);
          })
        );
      }),
      shareReplay(1),
      takeUntil(this.destroy$)
    );

    // Load balances for the viewed address (with periodic refresh)
    this.walletBalance$ = combineLatest([
      this.resolvedAddress$,
      interval(10000).pipe(startWith(0)) // Refresh every 10 seconds
    ]).pipe(
      switchMap(([resolvedAddress]) => {
        if (!resolvedAddress) {
          return of(null);
        }
        return from(this.web3Svc.getBalance(resolvedAddress)).pipe(
          catchError((err) => {
            console.error('Error loading wallet balance:', err);
            return of(null);
          })
        );
      }),
      shareReplay(1),
      takeUntil(this.destroy$)
    );

    this.stashBalance$ = combineLatest([
      this.stashAddress$,
      this.stashDeployed$,
      interval(10000).pipe(startWith(0)) // Refresh every 10 seconds
    ]).pipe(
      switchMap(([stashAddress, stashDeployed]) => {
        if (!stashAddress || stashDeployed !== true) {
          return of(null);
        }
        return from(this.web3Svc.getBalance(stashAddress)).pipe(
          catchError((err) => {
            console.error('Error loading stash balance:', err);
            return of(null);
          })
        );
      }),
      shareReplay(1),
      takeUntil(this.destroy$)
    );

    // Start listening to route params FIRST - this should work for everyone
    this.route.params.pipe(
      switchMap((params: any) => {
        this.loading = true;
        const address = params.address.toLowerCase();
        if (address.includes('.eth')) return from(this.web3Svc.getEnsOwner(address));
        return of(address);
      }),
      
      tap((address) => {
        address = address.toLowerCase();
        if (!address) return;
        this.viewedProfileAddress = address;
        this.dataSvc.fetchOwned(address).pipe(take(1)).subscribe((res) => this.applyOwnedSnapshot(res));

        this.bids$ = this.dataSvc.fetchUserBids(address).pipe(
          tap((res) => {
            this.calcs = { ...this.calcs, placedBids: res.length };
            this.calculateValues(res, 'bids');
          })
        );

        this.bought$ = this.dataSvc.fetchBoughtEvents(address).pipe(
          map((events: Event[]) => withDashboardSaleDateTippyOptions(events)),
          tap((res) => this.calculateValues(res, 'spent'))
        );

        this.sold$ = this.dataSvc.fetchSoldEvents(address).pipe(
          map((events: Event[]) => withDashboardSaleDateTippyOptions(events)),
          tap((res) => this.calculateValues(res, 'earned'))
        );

        // Stash bids received (bids on owned punks)
        // Backend already returns only the highest bid per punk when filterPunkIds is used
        // We keep the original bids (not transformed to punk format) to avoid cartesian product
        this.stashBidsReceived$ = this.owned$.pipe(
          switchMap((owned) => {
            if (owned.length === 0) {
              return of([]);
            }
            // Extract punk IDs from owned punks
            const punkIds = owned.map(punk => parseInt(punk.id)).filter(id => !isNaN(id));
            if (punkIds.length === 0) {
              return of([]);
            }
            return this.dataSvc.fetchAllStashBids({
              filterPunkIds: punkIds,
              filterAmountMin: this.displayOnlyBidsAboveMinEthFromSettings()
                ? (environment.myBidsMinEth ?? 1)
                : undefined
            }).pipe(
              map((response) => response.bids || []),
              tap((bids) => {
                // Calculate stats for stash bids received
                // Count unique bids (not punks, as backend already returns one bid per punk)
                // Note: backend returns one bid per punk (the highest), so we count unique bid IDs
                const uniqueBidIds = new Set(bids.map((bid: any) => bid.id));
                const stashBidsReceived = uniqueBidIds.size;
                this.calcs = { ...this.calcs, stashPunksWithBids: stashBidsReceived };
                // Don't calculate total value as it would be a cartesian product (bid × punks)
              })
            );
          })
        );

        // Stash bids placed (bids by this account)
        this.stashBidsPlaced$ = combineLatest([
          this.displayArchivedStashBids$,
          this.stashBidsPlacedRefresh$
        ]).pipe(
          switchMap(([includeArchived]) => this.dataSvc.fetchAllStashBids({
            filterBidder: address,
            includeAllStatuses: includeArchived ? true : undefined,
            sortBy: includeArchived ? 'activeThenArchived' : undefined
          })),
          map((response) => {
            const bids = response.bids || [];
            // Transform stash bids to format compatible with punkItem template
            return bids.map((bid: any) => {
              // Extract first punk ID for display
              let firstPunkId: number | undefined;
              try {
                if (bid.punk_indices) {
                  let bidPunkIndices: number[] = [];
                  if (typeof bid.punk_indices === 'string') {
                    bidPunkIndices = JSON.parse(bid.punk_indices);
                  } else if (Array.isArray(bid.punk_indices)) {
                    bidPunkIndices = bid.punk_indices;
                  }
                  if (bidPunkIndices.length > 0) {
                    firstPunkId = bidPunkIndices[0];
                  }
                }
              } catch (error) {
                console.error('Error parsing punk_indices:', error);
              }
              return bid;
            });
          }),
          tap((bids) => {
            // Calculate stats for stash bids placed
            const stashBidsPlaced = bids.length;
            this.calcs = { ...this.calcs, stashPlacedBids: stashBidsPlaced };
            // Calculate total value of stash bids placed
            const totalValue = bids.reduce((sum, bid) => {
              const ethValue = parseFloat(bid.bid_amount_eth || '0');
              return sum + (ethValue * 1e18); // Convert to wei
            }, 0);
            this.calcs = { ...this.calcs, stashBids: BigInt(Math.floor(totalValue)) };
            this.calculateValues(bids.map(bid => ({
              value: bid.value,
              usd: bid.usd,
              blockTimestamp: bid.blockTimestamp
            })), 'stashBids');
          })
        );

        if (
          environment.collection === 'cryptopunks-v1' &&
          environment.featureFlags?.PUNKSMARKET_BID_SUPPORT !== false
        ) {
          this.punksmarketBidsPlaced$ = combineLatest([
            this.resolvedAddress$,
            this.punksmarketBidsPlacedRefresh$,
          ]).pipe(
            switchMap(([addr]) => {
              if (!addr) {
                return of([] as StashBid[]);
              }
              return this.dataSvc
                .fetchPunksmarketBids({
                  active: true,
                  bidder: addr,
                  limit: 100,
                  sort: 'bid_wei-desc',
                })
                .pipe(
                  map((res) =>
                    (res.items ?? []).map(
                      (item) =>
                        unifiedBidToStashTableShape(
                          mapPunksmarketIndexerBidToUnified(item),
                        ) as StashBid,
                    ),
                  ),
                );
            }),
            shareReplay(1),
          );
        }

        combineLatest([
          this.owned$,
          this.bids$,
          this.bought$,
          this.sold$,
          this.stashBidsReceived$,
          this.stashBidsPlaced$,
          this.punksmarketBidsPlaced$,
        ]).pipe(
          tap(() => {
            this.platformService.setTimeout(() => this.loading = false, 200);
          }),
          catchError((error) => {
            console.error('Error loading dashboard data:', error);
            this.loading = false;
            return EMPTY;
          })
        ).subscribe();
      }),
      takeUntil(this.destroy$)
    ).subscribe();

    // THEN listen to Web3 connection state changes SEPARATELY
    combineLatest([
      this.stateService.web3Connected$,
      this.stateService.walletAddress$
    ]).pipe(
      takeUntil(this.destroy$),
      tap(([isConnected, walletAddress]) => {
        this.isUserConnected = isConnected;
        this.userAddress = walletAddress;
        
        if (isConnected && walletAddress) {
          // Check if we're currently viewing the connected user's dashboard
          this.route.params.pipe(take(1)).subscribe(params => {
            const currentAddress = (params as any).address?.toLowerCase();
            if (currentAddress && walletAddress && currentAddress === walletAddress.toLowerCase()) {
              this.loadProxyPunks(currentAddress);
            }
          });
        } else {
          // Clear proxy punks when disconnected
          this.proxyPunks$ = of([]);
          this.c721ProxyPunks$ = of([]);
          this.legacyProxyPunks$ = of([]);
        }
      })
    ).subscribe();

    // Create observable for showAcceptButton
    this.showAcceptButton$ = combineLatest([
      this.stateService.web3Connected$,
      this.stateService.walletAddress$,
      this.route.params
    ]).pipe(
      map(([isConnected, walletAddress, params]) => {
        const currentAddress = (params as any).address?.toLowerCase();
        return !!(isConnected && walletAddress && currentAddress && currentAddress === walletAddress.toLowerCase());
      }),
      shareReplay(1),
      takeUntil(this.destroy$)
    );
  }

  ngOnInit(): void {
    this.loading = true;
  }

  ngOnDestroy(): void {
    window.removeEventListener('ownedWrapStatusChanged', this.handleOwnedWrapStatusChanged);
    this.destroy$.next();
    this.destroy$.complete();
    this.stashBidsPlacedRefresh$.complete();
    this.punksmarketBidsPlacedRefresh$.complete();
    this.ownedPunksSubject.complete();
  }

  private applyOwnedSnapshot(res: Punk[]): void {
    this.ownedPunksSubject.next(res);
    const wrapped = res.filter((punk) => punk.wrapped);
    const bidsReceived = res.map((punk) => punk.bid).filter((bid) => bid);
    const listed = res.map((punk) => punk.listing).filter((listing) => listing);
    this.ownedPunkIds = res.map((punk) => parseInt(punk.id, 10)).filter((id) => !isNaN(id));
    this.calcs = {
      ...this.calcs,
      punksOwned: res.length - wrapped.length,
      punksWithBids: bidsReceived.length,
      listed: listed.length,
      wrappedOwned: wrapped.length,
    };
    this.calculateValues(bidsReceived, 'bidsReceived');
  }

  private handleOwnedWrapStatusChanged = (domEvent: globalThis.Event): void => {
    if (!(domEvent instanceof CustomEvent)) {
      return;
    }

    const detail = domEvent.detail as {
      wallet?: string;
      updates?: Array<{ punkId: string; wrapped: boolean }>;
    };
    const wallet = detail?.wallet?.toLowerCase();
    const updates = detail?.updates;

    if (!wallet || !this.viewedProfileAddress || wallet !== this.viewedProfileAddress) {
      return;
    }
    if (!Array.isArray(updates) || updates.length === 0) {
      return;
    }

    const updateMap = new Map(updates.map((update) => [String(update.punkId), update.wrapped]));
    const patched = this.ownedPunksSubject.getValue().map((punk) => {
      const wrapped = updateMap.get(String(punk.id));
      return wrapped !== undefined ? { ...punk, wrapped } : punk;
    });

    this.applyOwnedSnapshot(patched);
  };



  private async loadProxyPunks(userAddress: string): Promise<void> {
    try {
      // Get C721 proxy
      const c721Proxy = await this.punkWrapperService.getPunkProxyForUser(
        userAddress
      );

      // Get legacy proxy
      const legacyProxy = await this.punkWrapperService.getProxyInfo(userAddress);

      // Load punks from C721 proxy if it exists
      if (c721Proxy) {
        this.c721ProxyPunks$ = this.dataSvc.fetchOwned(c721Proxy.toLowerCase());
      }

      // Load punks from legacy proxy if it exists
      if (legacyProxy) {
        this.legacyProxyPunks$ = this.dataSvc.fetchOwned(legacyProxy.toLowerCase());
      }

      // Combine owned punks with proxy punks, merging by ID to avoid duplicates
      this.proxyPunks$ = combineLatest([
        this.owned$,
        c721Proxy ? this.c721ProxyPunks$ : of([]),
        legacyProxy ? this.legacyProxyPunks$ : of([])
      ]).pipe(
        map(([owned, c721Punks, legacyPunks]) => {
          const punksById = new Map<string, any>();

          for (const punk of owned) {
            punksById.set(punk.id, { ...punk });
          }

          for (const punk of c721Punks) {
            const existing = punksById.get(punk.id);
            if (existing) {
              existing.proxyType = 'C721';
              existing.proxyAddress = c721Proxy;
            } else {
              punksById.set(punk.id, { ...punk, proxyType: 'C721', proxyAddress: c721Proxy });
            }
          }

          for (const punk of legacyPunks) {
            const existing = punksById.get(punk.id);
            if (existing) {
              existing.proxyType = 'Legacy';
              existing.proxyAddress = legacyProxy;
            } else {
              punksById.set(punk.id, { ...punk, proxyType: 'Legacy', proxyAddress: legacyProxy });
            }
          }

          return Array.from(punksById.values());
        })
      );

    } catch (error) {
      console.error('Error loading proxy punks:', error);
    }
  }

  calculateValues(events: any[], type: string): void {
    let weiValue = BigInt(0);
    let usdValue = 0;

    events.forEach((event) => {
      const ethVal = this.weiToEth.transform(event.value);
      weiValue += BigInt(event.value || '0')

      const usd = +event.usd || this.dataSvc.getUSDPrice();
      usdValue += this.usdPipe.transform(ethVal, String(usd), event.blockTimestamp);

      // if (type === 'spent') console.log({event, ethVal, usdValue})
    });

    this.calcs = {
      ...this.calcs,
      [type]: weiValue,
      [`${type}USD`]: usdValue,
    };
  }

  // async share(): Promise<void> {
  //   const routeParams = await firstValueFrom(this.route.params);
  //   const address = routeParams?.address?.toLowerCase();
  //   if (!address) return;
  //   this.dataSvc.fetchUserCard(address, this.param);
  //   const message = `CryptoPunks owned by ${address}\n\n`;
  //   const urlToShare = `https://punks.limo/${address}?=${this.param}`;
  //   const twitterIntentURL = `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(urlToShare)}`;
  //   window.open(twitterIntentURL, '_blank');
  // }

  async share(el: HTMLButtonElement): Promise<void> {
    const routeParams = await firstValueFrom(this.route.params);
    const address = routeParams?.address?.toLowerCase();
    if (!address) return;

    const buttonText = el.innerText;
    const buttonWidth = el.offsetWidth;

    el.innerText = 'Link Copied!';
    el.style.width = buttonWidth + 'px';
    el.style.textAlign = 'center';

    this.platformService.setTimeout(() => {
      el.innerText = buttonText;
      el.style.width = 'auto';
    }, 2000);

    const param = Date.now();
    this.dataSvc.fetchUserCard(address, param);
    const urlToShare = `${environment.siteUrl}/${address}?=${param}`;
    navigator.clipboard.writeText(urlToShare);
  }

  protected readonly environment = environment;
  protected readonly isOwnedPunkWrapped = isOwnedPunkWrapped;

  /** Sale JSON dump icon — local / dev builds only. */
  get showSaleDebugJson(): boolean {
    return environment.debugMode === true
      || !environment.production
      || (environment.env?.startsWith('dev') ?? false);
  }

  isDashboardPunksmarketNativeSale(item: {
    platform?: string | null;
    fromAccount?: { id?: string } | null;
    fromWallet?: string | null;
  }): boolean {
    return environment.collection === 'cryptopunks-v1' && isPunksmarketNativeSale(item);
  }

  isDashboardErc721StashSaleIcon(item: {
    type?: string;
    platform?: string | null;
  }): boolean {
    return (
      environment.collection === 'cryptopunks-v1' &&
      item.type === 'Sale' &&
      item.platform === 'erc721stash'
    );
  }

  isDashboardWrappedV1SaleIcon(item: {
    type?: string;
    platform?: string | null;
    wrapped?: boolean;
    fromAccount?: { id?: string } | null;
    fromWallet?: string | null;
  }): boolean {
    if (environment.collection !== 'cryptopunks-v1' || item.type !== 'Sale') {
      return false;
    }
    if (this.isDashboardPunksmarketNativeSale(item) || this.isDashboardErc721StashSaleIcon(item)) {
      return false;
    }
    const platform = item.platform ?? '';
    return (
      item.wrapped === true ||
      platform === 'punkv1' ||
      platform === 'ethereum'
    ) && platform !== 'blur' && platform !== 'opensea';
  }

  isDashboardNativeV1SaleIcon(item: {
    type?: string;
    platform?: string | null;
    wrapped?: boolean;
    fromAccount?: { id?: string } | null;
    fromWallet?: string | null;
  }): boolean {
    if (environment.collection !== 'cryptopunks-v1' || item.type !== 'Sale') {
      return false;
    }
    if (item.wrapped || this.isDashboardPunksmarketNativeSale(item) || this.isDashboardErc721StashSaleIcon(item)) {
      return false;
    }
    const platform = item.platform ?? '';
    return platform === 'punksv1' || platform === 'punksmarket' || platform === 'punkv1';
  }

  punksmarketPunkUrl(tokenId: string | number | undefined): string {
    return punksmarketAppPunkUrl(tokenId ?? '');
  }

  copySaleJson(item: unknown, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const sale = item as Record<string, unknown>;
    const payload = {
      ...sale,
      transactionHash: sale['transactionHash'] ?? sale['tx'] ?? null,
    };
    const jsonString = JSON.stringify(payload, null, 2);
    console.log('[DashboardComponent] Sale event JSON:', payload);

    const copy = (): void => {
      const target = event.target as HTMLElement;
      const originalText = target.textContent;
      target.textContent = 'Copied!';
      target.style.color = '#00ff00';
      this.platformService.setTimeout(() => {
        target.textContent = originalText;
        target.style.color = '';
      }, 1000);
    };

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(jsonString).then(copy).catch((err) => {
        console.error('Failed to copy sale JSON:', err);
      });
      return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = jsonString;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    copy();
  }

  // Owner bid methods
  async openCreateOwnerBid(address: string): Promise<void> {
    if (!address) return;

    // Fetch owned punks to get punk IDs
    try {
      const ownedPunks = await firstValueFrom(this.dataSvc.fetchOwned(address.toLowerCase()));
      
      // Extract punk IDs as numbers
      this.ownerBidPunkIndices = ownedPunks.map(punk => parseInt(punk.id)).sort((a, b) => a - b);
      this.ownerBidAddress = address.toLowerCase();
      this.ownerBidFilterTag = `owner=${address.toLowerCase()}`;
      this.ownerBidModalActive = true;

      console.log('[DashboardComponent] Opening owner bid modal for address:', address);
      console.log('[DashboardComponent] Punk indices:', this.ownerBidPunkIndices.length);
    } catch (error) {
      console.error('[DashboardComponent] Error fetching owned punks for owner bid:', error);
    }
  }

  closeOwnerBidModal(): void {
    this.ownerBidModalActive = false;
    this.ownerBidAddress = null;
    this.ownerBidPunkIndices = [];
    this.ownerBidFilterTag = '';
  }

  onOwnerBidCreated(): void {
    console.log('[DashboardComponent] Owner bid created successfully');
    this.closeOwnerBidModal();
  }

  private static readonly COPY_BID_AMOUNT_INCREMENT_ETH = 0.01;
  private static readonly EXPIRY_DAY_OPTIONS = [1, 3, 7, 30, 365];

  openCopyBidModal(bid: StashBid): void {
    this.dataSvc.fetchBidDetailsWithPunkIndices(bid.id).subscribe({
      next: (bidDetails: any) => {
        this.buildCopyBidPayload(bid, bidDetails);
      },
      error: () => {
        this.buildCopyBidPayload(bid, null);
      }
    });
  }

  private buildCopyBidPayload(bid: StashBid, bidDetails: any): void {
    const modalBidType: BidType = bid.bidType === 'custom' ? 'targeted'
      : (bid.bidType === 'owner' || bid.bidType === 'trait' || bid.bidType === 'collection')
        ? bid.bidType
        : 'collection';

    let punkIndices: number[] = [];
    let selectedPunkIds: number[] = [];

    const detailPunkIds = this.extractPunkIdsFromDetails(bidDetails);

    if (modalBidType === 'owner' || modalBidType === 'trait') {
      punkIndices = detailPunkIds.length > 0 ? detailPunkIds : (bid.punkIds ?? bid.punkIndices ?? []);
    } else if (modalBidType === 'targeted') {
      if (detailPunkIds.length > 0) {
        selectedPunkIds = detailPunkIds;
      } else {
        const rawIds = bid.selected_punk_ids ?? bid.punkIds ?? bid.punkIndices ?? [];
        selectedPunkIds = Array.isArray(rawIds)
          ? rawIds
          : typeof rawIds === 'string'
            ? rawIds.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n))
            : [];
      }
    }

    let expiryDays: number | undefined;
    if (bid.expires_at && bid.created_at) {
      const diffMs = new Date(bid.expires_at).getTime() - new Date(bid.created_at).getTime();
      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays > 0) {
        expiryDays = DashboardComponent.EXPIRY_DAY_OPTIONS.reduce((prev, curr) =>
          Math.abs(curr - diffDays) < Math.abs(prev - diffDays) ? curr : prev
        );
      }
    }

    this.copyFromBidPayload = {
      amountEth: bid.bid_amount_eth + DashboardComponent.COPY_BID_AMOUNT_INCREMENT_ETH,
      expiryDays,
      confidential: bid.confidential ?? false,
      bidType: modalBidType,
      ownerAddress: bid.ownerAddress ?? null,
      punkIndices,
      selectedPunkIds,
      activeTraits: bid.decodedTags ?? [],
      filterTag: bid.tag ?? ''
    };

    const allPunkIds = [...punkIndices, ...selectedPunkIds];
    if (modalBidType !== 'collection' && allPunkIds.length > 0) {
      this.copyConfirmationPunkIds = allPunkIds;
      this.copyConfirmationModalActive = true;
    } else {
      this.copyBidModalActive = true;
    }
  }

  private extractPunkIdsFromDetails(bidDetails: any): number[] {
    if (!bidDetails?.parsed_punk_indices) return [];
    if (bidDetails.parsed_punk_indices === 'all') return [];
    if (Array.isArray(bidDetails.parsed_punk_indices)) return bidDetails.parsed_punk_indices;
    return [];
  }

  onCopyConfirmed(): void {
    this.copyConfirmationModalActive = false;
    this.copyConfirmationPunkIds = [];
    this.copyBidModalActive = true;
  }

  onCopyCancelled(): void {
    this.copyConfirmationModalActive = false;
    this.copyConfirmationPunkIds = [];
    this.copyFromBidPayload = null;
  }

  closeCopyBidModal(): void {
    this.copyBidModalActive = false;
    this.copyFromBidPayload = null;
  }

  onCopyBidCreated(): void {
    this.closeCopyBidModal();
  }

  /**
   * Reads the "display only bids above min ETH" setting from localStorage (same key as my-bids component).
   * When true or unset, stash bids received are filtered by min ETH; when false, no min filter is applied.
   */
  private displayOnlyBidsAboveMinEthFromSettings(): boolean {
    if (!this.platformService.isClient) {
      return true;
    }
    const raw = this.platformService.getLocalStorageItem(this.MY_BIDS_STORAGE_KEY_MIN_ETH);
    return raw !== 'false';
  }

  onDisplayArchivedStashBidsToggle(): void {
    this.displayArchivedStashBids = !this.displayArchivedStashBids;
    this.displayArchivedStashBids$.next(this.displayArchivedStashBids);
    this.platformService.setLocalStorageItem(
      this.STASH_BIDS_DISPLAY_ARCHIVED_STORAGE_KEY,
      String(this.displayArchivedStashBids)
    );
  }

  private restoreDisplayArchivedStashBidsState(): void {
    if (!this.platformService.isClient) {
      return;
    }
    const saved = this.platformService.getLocalStorageItem(this.STASH_BIDS_DISPLAY_ARCHIVED_STORAGE_KEY);
    if (saved === 'true') {
      this.displayArchivedStashBids = true;
    } else if (saved === 'false') {
      this.displayArchivedStashBids = false;
    }
    this.displayArchivedStashBids$.next(this.displayArchivedStashBids);
  }

  canCancelStashBid = (bid: StashBid): boolean => {
    if (!this.isViewingConnectedWallet()) {
      return false;
    }
    return bid.uiStatusKind === 'active' && bid.bid_nonce != null;
  };

  async onCancelOutgoingStashBid(bid: StashBid): Promise<void> {
    if (!this.canCancelStashBid(bid) || this.outgoingCancelInProgress) {
      return;
    }

    const amount = Number(bid.bid_amount_eth || 0);
    this.openCancelDialog({
      title: 'Cancel bid',
      message: Number.isFinite(amount) && amount > 0
        ? `Are you sure you want to cancel this outgoing bid (${amount.toFixed(4)} ETH)?`
        : 'Are you sure you want to cancel this outgoing bid?',
      confirmLabel: 'Confirm cancel',
      destructive: true,
      action: {
        mode: 'single',
        bidNonce: bid.bid_nonce as number
      }
    });
  }

  async cancelAllOutgoingStashBids(stashBidsPlaced: StashBid[]): Promise<void> {
    if (!this.isViewingConnectedWallet() || this.outgoingCancelInProgress) {
      return;
    }

    if (stashBidsPlaced.length === 0) {
      this.openInfoDialog(
        'No active bids',
        'There are no active outgoing stash bids to cancel.'
      );
      return;
    }

    this.openCancelDialog({
      title: 'Cancel all active bids',
      message: `Are you sure you want to cancel all ${stashBidsPlaced.length} active outgoing stash bid(s)?`,
      confirmLabel: 'Confirm cancel all',
      destructive: true,
      action: {
        mode: 'all'
      }
    });
  }

  async onOutgoingCancelDialogConfirmed(): Promise<void> {
    if (!this.cancelDialogShowCancelButton) {
      this.closeCancelDialog();
      return;
    }

    const action = this.pendingCancelAction;
    this.closeCancelDialog();

    if (!action) {
      return;
    }

    await this.executeStashCancellation(action);
  }

  onOutgoingCancelDialogCancelled(): void {
    this.closeCancelDialog();
  }

  private openCancelDialog(config: {
    title: string;
    message: string;
    confirmLabel: string;
    destructive: boolean;
    action: { mode: 'single' | 'all'; bidNonce?: number };
  }): void {
    this.cancelDialogTitle = config.title;
    this.cancelDialogMessage = config.message;
    this.cancelDialogConfirmLabel = config.confirmLabel;
    this.cancelDialogDestructive = config.destructive;
    this.cancelDialogShowCancelButton = true;
    this.pendingCancelAction = config.action;
    this.cancelDialogActive = true;
  }

  private openInfoDialog(title: string, message: string): void {
    this.cancelDialogTitle = title;
    this.cancelDialogMessage = message;
    this.cancelDialogConfirmLabel = 'Close';
    this.cancelDialogDestructive = false;
    this.cancelDialogShowCancelButton = false;
    this.pendingCancelAction = null;
    this.cancelDialogActive = true;
  }

  private closeCancelDialog(): void {
    this.cancelDialogActive = false;
    this.pendingCancelAction = null;
  }

  private async executeStashCancellation(config: {
    mode: 'single' | 'all';
    bidNonce?: number;
  }): Promise<void> {
    if (!this.userAddress || !this.isViewingConnectedWallet()) {
      return;
    }

    this.outgoingCancelInProgress = true;
    try {
      const stashAddress = await this.web3Svc.getStashAddressFor(this.userAddress);
      const hash = config.mode === 'single'
        ? await this.web3Svc.cancelBidOnStash(stashAddress, config.bidNonce as number)
        : await this.web3Svc.cancelAllPunkBidsOnStash(stashAddress);

      if (!hash) {
        throw new Error('Transaction hash not returned');
      }

      await this.waitForReceipt(hash);
      if (config.mode === 'all') {
        try {
          await this.web3Svc.notifyIndexerInvalidateAllConfidentialBidsAfterCancelAll();
        } catch (e) {
          console.error('Error invalidating pending bids on indexer after cancel all:', e);
        }
      }
      this.refreshStashBidsPlaced();

      this.openInfoDialog(
        'Cancellation successful',
        config.mode === 'single'
          ? 'The outgoing bid has been canceled successfully.'
          : 'All active outgoing bids have been canceled successfully.'
      );
    } catch (error) {
      console.error('Error while canceling outgoing stash bids:', error);
      this.openInfoDialog(
        'Cancellation failed',
        'Unable to cancel bid(s). Please check your wallet and try again.'
      );
    } finally {
      this.outgoingCancelInProgress = false;
    }
  }

  private async waitForReceipt(hash: string): Promise<TransactionReceipt> {
    while (true) {
      try {
        const receipt = await this.web3Svc.waitForTransaction(hash);
        if (receipt) {
          return receipt;
        }
      } catch {
        // Keep polling until transaction is mined.
      }
    }
  }

  private refreshStashBidsPlaced(): void {
    this.stashBidsPlacedRefresh$.next(this.stashBidsPlacedRefresh$.value + 1);
  }

  private refreshPunksmarketBidsPlaced(): void {
    this.punksmarketBidsPlacedRefresh$.next(
      this.punksmarketBidsPlacedRefresh$.value + 1,
    );
  }

  canCancelPunksmarketBid = (bid: StashBid): boolean => {
    return (
      this.isViewingConnectedWallet() &&
      environment.featureFlags?.PUNKSMARKET_BID_NATIVE !== false &&
      !!bid.punksmarketBidId
    );
  };

  async onCancelPunksmarketBid(bid: StashBid): Promise<void> {
    if (!bid.punksmarketBidId || this.punksmarketCancelInProgress) {
      return;
    }
    this.punksmarketCancelInProgress = true;
    try {
      const hash = await this.punksmarketV1Svc.cancelBid(bid.punksmarketBidId);
      if (hash) {
        await this.waitForReceipt(hash);
      }
      this.refreshPunksmarketBidsPlaced();
      this.openInfoDialog(
        'Bid canceled',
        onChainBidCancelSuccess,
      );
    } catch (error) {
      console.error('Error canceling PunksMarket bid:', error);
      this.openInfoDialog(
        'Cancellation failed',
        onChainBidCancelFailure,
      );
    } finally {
      this.punksmarketCancelInProgress = false;
    }
  }

  punksmarketBidsEnabled(): boolean {
    return (
      environment.collection === 'cryptopunks-v1' &&
      environment.featureFlags?.PUNKSMARKET_BID_SUPPORT !== false
    );
  }

  private isViewingConnectedWallet(): boolean {
    if (!this.isUserConnected || !this.userAddress) {
      return false;
    }
    const routeAddress = this.route.snapshot.params['address']?.toLowerCase();
    return !!routeAddress && routeAddress === this.userAddress.toLowerCase();
  }

  canAcceptStashBid = (bid: any): boolean => {
    // Check if user is viewing their own page and owns punks targeted by the bid
    if (!this.isUserConnected || !this.userAddress) {
      return false;
    }
    
    // Get current address from route synchronously (for the function)
    const currentAddress = this.route.snapshot.params['address']?.toLowerCase();
    if (!currentAddress || currentAddress !== this.userAddress.toLowerCase()) {
      return false;
    }
    
    // Check if bid targets any owned punks
    try {
      const serverMatch = this.stashBidServerMatchingOwnedIds(bid);
      if (serverMatch !== null) {
        return serverMatch.length > 0;
      }

      let bidPunkIndices: number[] = [];
      if (bid.punk_indices) {
        if (typeof bid.punk_indices === 'string') {
          bidPunkIndices = JSON.parse(bid.punk_indices);
        } else if (Array.isArray(bid.punk_indices)) {
          bidPunkIndices = bid.punk_indices;
        }
      }
      
      // If bid has specific punk indices, check if any match owned punks
      if (bidPunkIndices.length > 0 && !(bidPunkIndices.length === 3 && bid.punk_indices_count !== 3)) {
        return bidPunkIndices.some(id => this.ownedPunkIds.includes(id));
      }
      
      // Collection bid applies to all punks, so if user owns any punks, they can accept
      return this.ownedPunkIds.length > 0;
    } catch (error) {
      return false;
    }
  }

  onAcceptStashBid(bid: any): void {
    try {
      let matchingPunkIds: number[] = [];
      const serverMatch = this.stashBidServerMatchingOwnedIds(bid);
      if (serverMatch !== null && serverMatch.length > 0) {
        matchingPunkIds = serverMatch;
      } else {
        let bidPunkIndices: number[] = [];
        if (bid.punk_indices) {
          if (typeof bid.punk_indices === 'string') {
            bidPunkIndices = JSON.parse(bid.punk_indices);
          } else if (Array.isArray(bid.punk_indices)) {
            bidPunkIndices = bid.punk_indices;
          }
        }

        if (bidPunkIndices.length > 0) {
          matchingPunkIds = bidPunkIndices.filter(id => this.ownedPunkIds.includes(id));
        } else {
          matchingPunkIds = [...this.ownedPunkIds];
        }
      }
      
      if (matchingPunkIds.length === 0) {
        console.error('No matching punks found for this bid');
        return;
      }
      
      if (matchingPunkIds.length === 1) {
        // Only one punk matches - navigate directly
        this.router.navigate(['/', 'details', matchingPunkIds[0]], {
          queryParams: { 
            acceptBid: 'true',
            bidId: bid.id 
          }
        });
      } else {
        // Multiple punks match - open modal to select which one
        this.selectedStashBid = bid;
        this.matchingPunkIdsForBid = matchingPunkIds;
        this.selectPunkForBidModalActive = true;
      }
    } catch (error) {
      console.error('Error processing stash bid acceptance:', error);
    }
  }

  onPunkSelected(punkId: number): void {
    if (!this.selectedStashBid) {
      return;
    }
    
    this.selectPunkForBidModalActive = false;
    // Navigate to the selected punk's item-view page
    this.router.navigate(['/', 'details', punkId], {
      queryParams: { 
        acceptBid: 'true',
        bidId: this.selectedStashBid.id 
      }
    });
    // Reset
    this.selectedStashBid = null;
    this.matchingPunkIdsForBid = [];
  }

  closeSelectPunkModal(): void {
    this.selectPunkForBidModalActive = false;
    this.selectedStashBid = null;
    this.matchingPunkIdsForBid = [];
  }

  /**
   * Punk IDs from indexer `matching_owned_punks` when `filterPunkIds` was used (or JWT-owned match).
   * Returns null if the API did not attach a list (caller should use punk_indices fallback).
   */
  private stashBidServerMatchingOwnedIds(bid: any): number[] | null {
    const raw = bid?.matching_owned_punks ?? bid?.matchingOwnedPunks;
    if (!Array.isArray(raw) || raw.length === 0) {
      return null;
    }
    const nums = raw
      .map((v: unknown) => (typeof v === 'string' ? Number.parseInt(v, 10) : Number(v)))
      .filter((id: number) => Number.isInteger(id) && id >= 0);
    const owned = new Set(this.ownedPunkIds);
    const filtered = nums.filter((id) => owned.has(id));
    return filtered.length > 0 ? filtered : null;
  }
}
